import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  HiOutlineHome,
  HiOutlineShoppingCart,
  HiHome,
  HiShoppingCart,
  HiOutlineLink,
} from "react-icons/hi";
import { FaPlay, FaMapMarkerAlt } from "react-icons/fa";
import useTheme from "../../hooks/useTheme";
import {
  getBottomTabActiveClasses,
  getBottomTabInactiveClasses,
  getBottomTabRippleClasses,
  getBottomTabBgClasses,
  getCartBadgeClasses,
} from "../../styles/theme";

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cartItems = useSelector((state) => state.cart.cart);
  const { profile } = useSelector((state) => state.profile);
  const theme = useTheme();

  // Calculate total cart items
  const totalCartItems = cartItems
    ? cartItems.reduce((total, item) => total + (item.quantity || 0), 0)
    : 0;

  const handleConnect = () => {
    navigate(`${profile?.key}/connect`);
  };

  const handleReels = () => {
    navigate(`${profile?.key}/reels`);
  };

  const handleMap = () => {
    if (profile?.map_url) {
      // Open Google Maps link in new tab
      window.open(profile.map_url, "_blank", "noopener,noreferrer");
    } else {
      // Fallback to connect page if no map URL
      navigate(`${profile?.key}/connect`);
    }
  };

  const isActive = (path) => {
    // Get profile key from Redux or extract from URL
    const profileKey = profile?.key || location.pathname.split("/")[1];

    if (!profileKey) return false;

    // For home tab, check if we're at the profile key path
    if (path === "/") {
      const isHomeActive =
        location.pathname === `/${profileKey}` ||
        location.pathname === `/${profileKey}/`;
      return isHomeActive;
    }
    // For other tabs, check the full path with profile key
    const isOtherActive = location.pathname === `/${profileKey}${path}`;
    return isOtherActive;
  };

  return (
    <div
      className={`md:hidden fixed bottom-0 left-0 right-0 z-50 border-t shadow-[0_-4px_20px_rgba(0,0,0,0.08)] ${getBottomTabBgClasses(
        theme
      )}`}
    >
      <div className="flex items-center justify-around py-4 px-4 max-w-screen-sm mx-auto">
        {/* Home Tab */}
        <button
          onClick={() => navigate(`/${profile?.key}`)}
          className={`group relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-2xl transition-all duration-500 ease-out transform ${
            isActive("/")
              ? getBottomTabActiveClasses(theme)
              : getBottomTabInactiveClasses(theme)
          }`}
          style={
            isActive("/") ? { color: theme.colors.bottomTabActiveText } : {}
          }
        >
          {/* Ripple effect background */}
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-300 ${getBottomTabRippleClasses(
              theme,
              isActive("/")
            )}`}
            style={{ animationIterationCount: "1", animationDuration: "0.6s" }}
          ></div>

          {isActive("/") ? (
            <HiHome className="w-7 h-7 mb-1 transition-all duration-300 transform group-hover:scale-110 relative z-10" />
          ) : (
            <HiOutlineHome className="w-7 h-7 mb-1 transition-all duration-300 transform group-hover:rotate-12 relative z-10" />
          )}
          <span
            className={`text-xs font-semibold transition-all duration-300 relative z-10 ${
              isActive("/") ? "scale-105" : ""
            }`}
          >
            Home
          </span>
        </button>

        {/* Cart Tab - Only show if cart is enabled */}

        <button
          onClick={() => navigate(`${profile?.key}/orders`)}
          className={`group relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-2xl transition-all duration-500 ease-out transform ${
            isActive("/orders")
              ? getBottomTabActiveClasses(theme)
              : getBottomTabInactiveClasses(theme)
          }`}
          style={
            isActive("/orders")
              ? { color: theme.colors.bottomTabActiveText }
              : {}
          }
        >
          {/* Ripple effect background */}
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-300 ${getBottomTabRippleClasses(
              theme,
              isActive("/orders")
            )}`}
            style={{ animationIterationCount: "1", animationDuration: "0.6s" }}
          ></div>

          {isActive("/orders") ? (
            <HiShoppingCart className="w-7 h-7 mb-1 transition-all duration-300 transform group-hover:scale-110 relative z-10" />
          ) : (
            <HiOutlineShoppingCart className="w-7 h-7 mb-1 transition-all duration-300 transform group-hover:rotate-12 relative z-10" />
          )}
          <span
            className={`text-xs font-semibold transition-all duration-300 relative z-10 ${
              isActive("/orders") ? "scale-105" : ""
            }`}
          >
            Cart
          </span>

          {/* Cart badge */}
          {totalCartItems > 0 && (
            <div
              className={`absolute -top-2 -right-2 text-xs font-bold rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center shadow-lg border-2 border-white z-20 transition-all duration-300`}
              style={{
                backgroundColor: theme.colors.cartBadgeGradientFrom,
                color: theme.colors.cartBadgeText,
              }}
            >
              {totalCartItems > 99 ? "99+" : totalCartItems}
            </div>
          )}
        </button>

        {/* Reels Tab or Map Tab - Show based on reel count */}
        {profile?.reel > 0 ? (
          <button
            onClick={handleReels}
            className={`group relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-2xl transition-all duration-500 ease-out transform ${
              isActive("/reels")
                ? getBottomTabActiveClasses(theme)
                : getBottomTabInactiveClasses(theme)
            }`}
            style={
              isActive("/reels")
                ? { color: theme.colors.bottomTabActiveText }
                : {}
            }
          >
            {/* Ripple effect background */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 ${getBottomTabRippleClasses(
                theme,
                isActive("/reels")
              )}`}
              style={{
                animationIterationCount: "1",
                animationDuration: "0.6s",
              }}
            ></div>

            <FaPlay className="w-6 h-6 mb-1 transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-12 relative z-10" />
            <span
              className={`text-xs font-semibold transition-all duration-300 relative z-10 ${
                isActive("/reels") ? "scale-105" : ""
              }`}
            >
              Reels
            </span>
          </button>
        ) : (
          <button
            onClick={handleMap}
            className={`group relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-2xl transition-all duration-500 ease-out transform ${getBottomTabInactiveClasses(
              theme
            )}`}
          >
            <FaMapMarkerAlt className="w-6 h-6 mb-1 transition-all duration-300 transform group-hover:scale-110 group-hover:-rotate-12 relative z-10" />
            <span className="text-xs font-semibold transition-all duration-300 relative z-10">
              Map
            </span>
          </button>
        )}

        {/* Connect Tab */}
        <button
          onClick={handleConnect}
          className={`group relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-2xl transition-all duration-500 ease-out transform ${
            isActive("/connect")
              ? getBottomTabActiveClasses(theme)
              : getBottomTabInactiveClasses(theme)
          }`}
          style={
            isActive("/connect")
              ? { color: theme.colors.bottomTabActiveText }
              : {}
          }
        >
          {/* Ripple effect background */}
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-300 ${getBottomTabRippleClasses(
              theme,
              isActive("/connect")
            )}`}
            style={{ animationIterationCount: "1", animationDuration: "0.6s" }}
          ></div>

          <HiOutlineLink className="w-7 h-7 mb-1 transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-12 relative z-10" />
          <span
            className={`text-xs font-semibold transition-all duration-300 relative z-10 ${
              isActive("/connect") ? "scale-105" : ""
            }`}
          >
            Connect
          </span>
        </button>
      </div>
    </div>
  );
};

export default MobileBottomNav;
